import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';

import { PresentationApiService } from 'src/app/api/services/presentation-api.service';
import { ScheduleApiService } from 'src/app/api/services/schedule-api.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';

const ASSET_SEARCH = {
  sortBy: 'changeDate',
  reverse: true,
  count: 1,
};

@Injectable({
  providedIn: 'root',
})
export class CompanyAssetsService {
  private scheduleListCache?: Promise<{ items?: unknown[] }>;
  private presentationListCache?: Promise<{ items?: unknown[] }>;
  private hasSchedulesCompleted?: boolean;
  private hasPresentationsCompleted?: boolean;
  private scheduleCreatedSubscription: Subscription | null = null;
  private presentationCreatedSubscription: Subscription | null = null;
  private generation = 0;

  constructor(
    private scheduleApiService: ScheduleApiService,
    private presentationApiService: PresentationApiService,
    private broadcaster: BroadcasterService,
  ) {
    this.broadcaster.on('risevision.company.selectedCompanyChanged', () => {
      this.reset();
    });
  }

  hasSchedules(): Promise<boolean> {
    if (this.hasSchedulesCompleted) {
      return Promise.resolve(true);
    }

    const requestGeneration = this.generation;

    return this.executeScheduleList().then((resp) => {
      if (requestGeneration !== this.generation) {
        return !!(resp?.items && resp.items.length > 0);
      }

      this.hasSchedulesCompleted = !!(resp?.items && resp.items.length > 0);

      if (!this.hasSchedulesCompleted) {
        this.addScheduleListener();
      }

      return this.hasSchedulesCompleted;
    });
  }

  hasPresentations(): Promise<boolean> {
    if (this.hasPresentationsCompleted) {
      return Promise.resolve(true);
    }

    const requestGeneration = this.generation;

    return this.executePresentationList().then((resp) => {
      if (requestGeneration !== this.generation) {
        return !!(resp?.items && resp.items.length > 0);
      }

      this.hasPresentationsCompleted = !!(resp?.items && resp.items.length > 0);

      if (!this.hasPresentationsCompleted) {
        this.addPresentationListener();
      }

      return this.hasPresentationsCompleted;
    });
  }

  private reset(): void {
    this.generation++;
    this.scheduleListCache = undefined;
    this.presentationListCache = undefined;
    this.hasSchedulesCompleted = undefined;
    this.hasPresentationsCompleted = undefined;
    this.clearScheduleListener();
    this.clearPresentationListener();
  }

  private executeScheduleList(): Promise<{ items?: unknown[] }> {
    if (!this.scheduleListCache) {
      this.scheduleListCache = this.scheduleApiService.list(ASSET_SEARCH);
    }

    return this.scheduleListCache;
  }

  private executePresentationList(): Promise<{ items?: unknown[] }> {
    if (!this.presentationListCache) {
      this.presentationListCache =
        this.presentationApiService.list(ASSET_SEARCH);
    }

    return this.presentationListCache;
  }

  private addScheduleListener(): void {
    if (!this.scheduleCreatedSubscription) {
      this.scheduleCreatedSubscription = this.broadcaster.on(
        'scheduleCreated',
        () => {
          this.hasSchedulesCompleted = true;
          this.clearScheduleListener();
          this.broadcaster.emit('companyAssetsUpdated');
        },
      );
    }
  }

  private addPresentationListener(): void {
    if (!this.presentationCreatedSubscription) {
      this.presentationCreatedSubscription = this.broadcaster.on(
        'presentationCreated',
        () => {
          this.hasPresentationsCompleted = true;
          this.clearPresentationListener();
          this.broadcaster.emit('companyAssetsUpdated');
        },
      );
    }
  }

  private clearScheduleListener(): void {
    if (this.scheduleCreatedSubscription) {
      this.scheduleCreatedSubscription.unsubscribe();
      this.scheduleCreatedSubscription = null;
    }
  }

  private clearPresentationListener(): void {
    if (this.presentationCreatedSubscription) {
      this.presentationCreatedSubscription.unsubscribe();
      this.presentationCreatedSubscription = null;
    }
  }
}
